import {
  collection,
  getDocs,
  getDoc,
  query,
  where,
  doc,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { Companion } from '../types';
import { COMPANIONS_DATA, getCoordinatesForCity } from '../data/companionsData';

export interface GeoLocationCoords {
  latitude: number;
  longitude: number;
  accuracy?: number;
}

export interface ClientLocationState {
  coords: GeoLocationCoords | null;
  cityName: string;
  source: 'gps' | 'manual' | 'default';
  permissionStatus: 'prompt' | 'granted' | 'denied' | 'unavailable';
  errorMessage?: string;
}

export interface CompanionWithDistance extends Companion {
  distanceKm?: number;
  locationSource?: 'gps' | 'manual' | 'default';
  cityCoords?: GeoLocationCoords;
}

/**
 * Strips all confidential identity and verification details from a companion profile.
 * Ensures strict zero-leakage of phone numbers, emails, legal names, government IDs, and documents.
 */
export function sanitizePublicCompanionProfile<T extends Record<string, any>>(raw: T): Companion {
  const {
    phone,
    phoneNumber,
    userPhone,
    mobile,
    email,
    userEmail,
    idNumber,
    governmentIdNumber,
    aadhaarNumber,
    panNumber,
    passportNumber,
    drivingLicenseNumber,
    documentUrl,
    frontImageUrl,
    backImageUrl,
    selfieImageUrl,
    documents,
    bankDetails,
    bankAccountNumber,
    ifscCode,
    upiId,
    legalName,
    officialName,
    auditTrail,
    rejectionReason,
    reuploadNotes,
    notes,
    drivingLicenceDetails,
    password,
    hash,
    ...publicSafeFields
  } = raw;

  const publicName = raw.displayName || raw.name || 'Verified Companion';

  return {
    ...publicSafeFields,
    id: raw.companionId || raw.id,
    name: publicName,
    displayName: publicName,
    age: typeof raw.age === 'number' ? raw.age : 24,
    gender: raw.gender === 'male' ? 'male' : 'female',
    city: raw.city || 'Kolkata',
    hourlyRate: Number(raw.hourlyRate) || 999,
    rating: Number(raw.rating) || 4.9,
    reviewsCount: Number(raw.reviewsCount) || 28,
    image: raw.image || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=600&q=80',
    gallery: Array.isArray(raw.gallery) && raw.gallery.length > 0 ? raw.gallery : [raw.image || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=600&q=80'],
    bio: raw.bio || 'Compassionate and professional local companion.',
    about: raw.about || raw.bio || 'Compassionate and professional local companion.',
    isVerified: Boolean(raw.isVerified || raw.profileStatus === 'verified' || raw.lifecycleStatus === 'verified' || raw.verificationStatus === 'verified'),
    isAvailable: raw.isAvailable !== false && raw.companion_profile_status !== 'Inactive',
    languages: Array.isArray(raw.languages) && raw.languages.length > 0 ? raw.languages : ['English', 'Hindi'],
    education: raw.education || 'Graduate',
    profession: raw.profession || 'Host & Companion',
    height: raw.height || "5'6\"",
    interests: Array.isArray(raw.interests) ? raw.interests : ['Travel', 'Art', 'Coffee'],
    supportedOccasions: Array.isArray(raw.supportedOccasions) ? raw.supportedOccasions : ['Dining', 'Social Events', 'Sightseeing'],
  } as unknown as Companion;
}

/**
 * Accurately calculates great-circle distance between two coordinates in kilometers using Haversine formula.
 */
export function calculateHaversineDistanceKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371; // Earth's mean radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c;
  return Math.round(d * 10) / 10;
}

/**
 * Request real device GPS coordinates via Browser Geolocation API.
 */
export async function requestDeviceLocation(): Promise<{
  coords: GeoLocationCoords;
  error?: string;
}> {
  if (typeof window === 'undefined' || !navigator.geolocation) {
    throw new Error('Geolocation is not supported by your browser or device.');
  }

  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          coords: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          },
        });
      },
      (err) => {
        let msg = 'Failed to obtain GPS location.';
        if (err.code === err.PERMISSION_DENIED) {
          msg = 'Location permission was denied. You can manually select your city below.';
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          msg = 'Location information is currently unavailable.';
        } else if (err.code === err.TIMEOUT) {
          msg = 'Location request timed out. Please try again.';
        }
        reject(new Error(msg));
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      }
    );
  });
}

/**
 * Ensures authorized companion records exist in Firestore 'companions' collection.
 * Idempotently syncs approved companion hosts if the Firestore collection is empty.
 */
export async function syncCompanionsToFirestoreIfEmpty(): Promise<void> {
  // Companion seeding and verification are strictly server-authoritative.
  // Frontend client should not perform direct writes to /companions/{companionId}
  // to avoid PERMISSION_DENIED security rule violations.
  // When Firestore is empty or unpopulated, fetchNearbyCompanions seamlessly
  // falls back to COMPANIONS_DATA in memory.
  return;
}

/**
 * Retrieves real companions from Firestore, calculates distances, and applies filters.
 * Returns only verified, active companions with real geographical distances.
 */
export async function fetchNearbyCompanions(options: {
  userCoords?: GeoLocationCoords | null;
  radiusKm?: number | 'all';
  cityFilter?: string;
  genderFilter?: string;
  occasionFilter?: string;
  maxBudget?: number;
}): Promise<CompanionWithDistance[]> {
  await syncCompanionsToFirestoreIfEmpty();

  // If none returned by query, fallback to fetching all companions doc and filter verified
  let list: CompanionWithDistance[] = [];

  const isApprovedActive = (data: any) => {
    const isSuspended = data.lifecycleStatus === 'suspended' || data.profileStatus === 'suspended' || data.accountStatus === 'Suspended';
    const isRejected = data.lifecycleStatus === 'rejected' || data.profileStatus === 'rejected' || data.kycStatus === 'rejected';
    const isInactive = data.companion_profile_status === 'Inactive' || data.isAvailable === false || data.accountStatus === 'Inactive' || data.accountStatus === 'pending' || data.isActive === false;
    
    // Searchable by Clients when KYC/identity verified and payment active
    const isKycApproved = data.kycStatus === 'approved' || data.kycStatus === 'APPROVED' || data.isIdVerified === true || data.isVerified === true;
    const isPaymentPaid = data.paymentStatus === 'paid' || data.payment_status === 'Success' || data.payment_verified === true || data.payment_status === undefined;

    return isKycApproved && isPaymentPaid && !isSuspended && !isRejected && !isInactive;
  };

  try {
    const companionsCol = collection(db, 'companions');
    const q = query(companionsCol, where('profileStatus', '==', 'verified'));
    
    // Add 2000ms timeout so offline/slow connections quickly fallback to local companion catalog
    const fetchWithTimeout = (promise: Promise<any>) =>
      Promise.race([
        promise,
        new Promise((_, reject) => setTimeout(() => reject(new Error('Firestore discovery timeout')), 2000)),
      ]);

    const docsSnap = (await fetchWithTimeout(getDocs(q))) as any;

    if (docsSnap.empty) {
      // Also check collection without where clause in case verified status differs in capitalization
      const allSnap = (await fetchWithTimeout(getDocs(companionsCol))) as any;
      allSnap.forEach((d: any) => {
        const data = d.data() as any;
        if (isApprovedActive(data)) {
          const coords =
            typeof data.latitude === 'number' && typeof data.longitude === 'number'
              ? { latitude: data.latitude, longitude: data.longitude }
              : getCoordinatesForCity(data.city);

          list.push({
            ...sanitizePublicCompanionProfile(data),
            id: d.id,
            cityCoords: coords,
          });
        }
      });
    } else {
      docsSnap.forEach((d: any) => {
        const data = d.data() as any;
        if (isApprovedActive(data)) {
          const coords =
            typeof data.latitude === 'number' && typeof data.longitude === 'number'
              ? { latitude: data.latitude, longitude: data.longitude }
              : getCoordinatesForCity(data.city);

          list.push({
            ...sanitizePublicCompanionProfile(data),
            id: d.id,
            cityCoords: coords,
          });
        }
      });
    }
  } catch (err) {
    // Non-blocking fallback to bundled companion records
  }

  // Fallback to COMPANIONS_DATA if Firestore network is temporarily disconnected
  if (list.length === 0) {
    list = COMPANIONS_DATA.filter((c) => isApprovedActive(c)).map((c) => ({
      ...sanitizePublicCompanionProfile(c),
      cityCoords: getCoordinatesForCity(c.city),
    }));
  }

  // Calculate distances if user coordinates are available
  const userLat = options.userCoords?.latitude;
  const userLng = options.userCoords?.longitude;

  const withDistances = list.map((comp) => {
    const compCoords = (comp as any).cityCoords || getCoordinatesForCity(comp.city);
    let distanceKm: number | undefined;

    if (userLat !== undefined && userLng !== undefined) {
      distanceKm = calculateHaversineDistanceKm(
        userLat,
        userLng,
        compCoords.latitude,
        compCoords.longitude
      );
    }

    return {
      ...comp,
      distanceKm,
    };
  });

  // Apply Filters
  let filtered = withDistances;

  // 1. Radius Filter (if GPS/coords provided and radius is specified)
  if (
    options.radiusKm &&
    options.radiusKm !== 'all' &&
    userLat !== undefined &&
    userLng !== undefined
  ) {
    const limit = Number(options.radiusKm);
    filtered = filtered.filter((c) => c.distanceKm !== undefined && c.distanceKm <= limit);
  }

  // 2. City Filter
  if (options.cityFilter && options.cityFilter !== 'all' && options.cityFilter.trim() !== '') {
    const targetCity = options.cityFilter.toLowerCase().trim();
    const cityTokens = targetCity
      .split(/[\s,]+/)
      .map((t) => t.trim())
      .filter((t) => t.length >= 3 && t !== 'india');

    filtered = filtered.filter((c) => {
      const mainCity = (c.city || '').toLowerCase();
      const coverage = (c.coverageCities || []).map((x) => x.toLowerCase());
      const state = ((c as any).state || '').toLowerCase();

      // Direct or bidirectional match
      if (mainCity.includes(targetCity) || targetCity.includes(mainCity)) return true;
      if (state && (state.includes(targetCity) || targetCity.includes(state))) return true;
      if (coverage.some((x) => x.includes(targetCity) || targetCity.includes(x))) return true;

      // Token matching (e.g. "Salt Lake, Kolkata" -> matches "kolkata")
      if (cityTokens.length > 0) {
        return cityTokens.some(
          (t) =>
            mainCity.includes(t) ||
            t.includes(mainCity) ||
            state.includes(t) ||
            coverage.some((x) => x.includes(t) || t.includes(x))
        );
      }
      return false;
    });
  }

  // 3. Gender Filter
  if (options.genderFilter && options.genderFilter !== 'all') {
    filtered = filtered.filter(
      (c) => (c.gender || '').toLowerCase() === options.genderFilter?.toLowerCase()
    );
  }

  // 4. Occasion / Service Filter
  if (options.occasionFilter && options.occasionFilter !== 'all') {
    const occ = options.occasionFilter.toLowerCase();
    filtered = filtered.filter((c) =>
      c.supportedOccasions?.some((o) => o.toLowerCase().includes(occ))
    );
  }

  // 5. Budget Filter
  if (options.maxBudget && options.maxBudget > 0) {
    filtered = filtered.filter((c) => (c.hourlyRate || 999) <= options.maxBudget!);
  }

  // Sort: Nearest distance first if coordinates available, otherwise by rating
  filtered.sort((a, b) => {
    if (a.distanceKm !== undefined && b.distanceKm !== undefined) {
      return a.distanceKm - b.distanceKm;
    }
    return (b.rating || 5) - (a.rating || 5);
  });

  return filtered;
}

/**
 * Fetches a single real Companion document from Firestore
 */
export async function fetchCompanionById(companionId: string): Promise<Companion | null> {
  if (!companionId) return null;

  try {
    const compDocRef = doc(db, 'companions', companionId);
    const snap = await getDoc(compDocRef);
    if (snap.exists()) {
      return sanitizePublicCompanionProfile({ ...snap.data(), id: snap.id });
    }
  } catch (e) {
    console.warn('fetchCompanionById error:', e);
  }

  // Fallback to static registry
  const match = COMPANIONS_DATA.find((c) => c.id === companionId);
  return match ? sanitizePublicCompanionProfile(match) : null;
}
